import { useMemo } from 'react';
import type { Medication } from '@campusmeds/shared';
import { WorkflowStatus } from '@campusmeds/shared';

export interface MetricsData {
  totalStudents: number;
  premiumUsers: number;
  activeMedications: number;
  successfulPickupsMonthly: number;
  successfulPickupsAllTime: number;
  averageTimeToPickup: number; // in days
  pharmacySuccessRate: number; // percentage
}

interface Patient {
  id: string;
  tier?: 'FREE' | 'PRO';
  email?: string;
  createdAt: Date;
  updatedAt: Date;
}

interface UseMetricsProps {
  medications: Medication[];
  patients: Patient[];
}

export function useMetrics({ medications, patients }: UseMetricsProps): MetricsData {
  return useMemo(() => {
    // 1. Total Students - all patients are students
    const totalStudents = patients.length;

    // 2. Premium Users - count patients with PRO tier
    const premiumUsers = patients.filter(p => p.tier === 'PRO').length;

    // 3. Active Medications - count medications not INACTIVE
    const activeMedications = medications.filter(
      m => m.workflowStatus !== WorkflowStatus.INACTIVE
    ).length;

    // 4. Successful Pickups - medications with lastPickupDate
    const medicationsWithPickup = medications.filter(m => m.lastPickupDate);

    // Monthly - pickups in current month
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const successfulPickupsMonthly = medicationsWithPickup.filter(m => {
      if (!m.lastPickupDate) return false;
      const pickupDate = new Date(m.lastPickupDate);
      return pickupDate.getMonth() === currentMonth &&
             pickupDate.getFullYear() === currentYear;
    }).length;

    // All-time - total medications with pickup
    const successfulPickupsAllTime = medicationsWithPickup.length;

    // 5. Average Time-to-Pickup
    // Calculate average days between lastNotification and lastPickupDate
    let totalDays = 0;
    let countWithBothDates = 0;

    medicationsWithPickup.forEach(m => {
      if (m.lastNotification && m.lastPickupDate) {
        const notificationDate = new Date(m.lastNotification);
        const pickupDate = new Date(m.lastPickupDate);
        const diffTime = pickupDate.getTime() - notificationDate.getTime();
        const diffDays = diffTime / (1000 * 3600 * 24);

        // Only count positive values (pickup after notification)
        if (diffDays >= 0 && diffDays <= 90) {
          totalDays += diffDays;
          countWithBothDates++;
        }
      }
    });

    const averageTimeToPickup = countWithBothDates > 0
      ? Math.round(totalDays / countWithBothDates * 10) / 10 // Round to 1 decimal
      : 0;

    // 6. Pharmacy Success Rate
    // Percentage of IN_STOCK notifications vs total notifications
    const medicationsWithNotifications = medications.filter(
      m => m.notificationType === 'IN_STOCK' || m.notificationType === 'OUT_OF_STOCK'
    );

    const inStockCount = medications.filter(
      m => m.notificationType === 'IN_STOCK'
    ).length;

    const pharmacySuccessRate = medicationsWithNotifications.length > 0
      ? Math.round((inStockCount / medicationsWithNotifications.length) * 100)
      : 0;

    return {
      totalStudents,
      premiumUsers,
      activeMedications,
      successfulPickupsMonthly,
      successfulPickupsAllTime,
      averageTimeToPickup,
      pharmacySuccessRate
    };
  }, [medications, patients]);
}
